import { Pencil, StickyNote } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateProject } from '../../hooks/use-projects';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

interface ProjectNotesPanelProps {
  projectId: string;
  notes: string;
  canEdit: boolean;
}

export function ProjectNotesPanel({
  projectId,
  notes,
  canEdit,
}: ProjectNotesPanelProps): JSX.Element | null {
  const { t } = useTranslation();
  const update = useUpdateProject(projectId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes);

  useEffect(() => {
    setDraft(notes);
  }, [notes]);

  const empty = !notes.trim();
  if (empty && !canEdit) return null;

  return (
    <section className="rounded-md border border-amber-200 bg-amber-50/70 px-4 py-3 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-amber-900">
          <StickyNote className="h-4 w-4" />
          <span className="text-sm font-semibold">{t('notes.projectTitle')}</span>
        </div>
        {canEdit && !editing && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-amber-900 hover:bg-amber-100"
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-3.5 w-3.5" />
            <span className="ml-1 text-xs">{t('notes.edit')}</span>
          </Button>
        )}
      </header>

      {editing ? (
        <div className="mt-2 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('notes.projectPlaceholder')}
            className="min-h-[120px] border-amber-200 bg-white text-sm focus-visible:ring-amber-400"
            maxLength={20_000}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft(notes);
                setEditing(false);
              }}
              disabled={update.isPending}
            >
              {t('notes.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                await update.mutateAsync({ notes: draft });
                setEditing(false);
              }}
              disabled={update.isPending}
            >
              {update.isPending ? t('notes.saving') : t('notes.save')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-amber-950/90">
          {empty ? (
            <span className="italic text-amber-800/70">{t('notes.projectEmptyView')}</span>
          ) : (
            notes
          )}
        </div>
      )}
    </section>
  );
}
